import { createApp } from 'vue'
import App from './App.vue'
import {
  initViewportHeight,
  persistChannelFromLocation,
  setDomFontSize,
} from '@core/utils/index.js'
import { pinia } from '@core/store/pinia.js'
import { useConfigStore } from '@core/store/config.js'
import router from '@core/router/index.js'
import i18n from '@core/lang/index.js'
import Vant from 'vant'

import 'vant/lib/index.css'
import './style.scss'
import { adjustInit } from '@core/utils/fbq.js'
import {
  initPwaInstallTracking,
  isFacebookInAppBrowser,
  isIOS,
} from '@core/utils/pwa.js'
import { handleFacebookInAppOpen } from '@core/utils/facebookLanding.js'
import { initMgsPwaEventReporting } from '@core/utils/mgsExtension.js'
import { initPushMessaging, syncPushTokenOnLogin, bindPushRouter } from '@core/utils/push.js'
import eventBus from '@core/utils/eventBus.js'
import { showAuthPopup } from '@components/popup/auth/index.js'

const setupPwaUpdateReload = () => {
  if (!('serviceWorker' in navigator)) return

  const hadController = !!navigator.serviceWorker.controller
  let refreshing = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) return
    if (refreshing) return
    refreshing = true
    window.location.reload()
  })
}

setupPwaUpdateReload()

// PWA 从 bfcache 恢复时可能仍运行旧版 JS，强制刷新以避免 chunk 404
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    window.location.reload()
  }
})

persistChannelFromLocation()
initPwaInstallTracking()
initMgsPwaEventReporting()
initPushMessaging()
bindPushRouter(router)

eventBus.on('loginSuccess', () => {
  void syncPushTokenOnLogin()
})

eventBus.on('register', (tab = 'register') => {
  showAuthPopup({ tab: tab === 'login' ? 'login' : 'register' })
})

let wakeLock = null;
let requesting = false;

const requestWakeLock = async () => {
  if (!('wakeLock' in navigator)) return;
  if (requesting) return;
  if (wakeLock && !wakeLock.released) return;
  if (document.visibilityState !== 'visible') return;

  requesting = true;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => {
      wakeLock = null;
    }, { once: true });
    console.log('屏幕常亮已启用');
  } catch (err) {
    console.log(`${err.name}, ${err.message}`);
  } finally {
    requesting = false;
  }
};

document.addEventListener('click', requestWakeLock, { once: true });

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && (!wakeLock || wakeLock.released)) {
    requestWakeLock();
  }
});

initViewportHeight()

const waitBasicInfo = async (basicPromise, timeoutMs = 3000) => {
  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('getBasicInfo timeout')), timeoutMs)
    Promise.resolve(basicPromise).then(
      (res) => {
        clearTimeout(timer)
        resolve(res)
      },
      (err) => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

const main = async () => {
  adjustInit()
  setDomFontSize()

  if (isIOS && isFacebookInAppBrowser()) {
    handleFacebookInAppOpen()
    return
  }

  const app = createApp(App)

  app.use(router)
    .use(pinia)
    .use(Vant)
    .use(i18n)

  const configStore = useConfigStore()
  const basicPromise = configStore.getBasicInfo()

  if (isFacebookInAppBrowser()) {
    try {
      await waitBasicInfo(basicPromise)
    } catch (e) {
      console.warn('getBasicInfo failed', e)
    }

    if (handleFacebookInAppOpen({ is_landing: configStore.is_landing })) {
      return
    }
  }

  app.mount('#app')
}

main()

